import React, { useCallback, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useDashboardStore } from '../../store/dashboardStore';
import { NewPatientData } from '../../types';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';

const RequiredMark = () => <span className="text-red-600">※必須</span>;

/** 未入力時にハイライトする項目（名前・ローマ字＋保護者あり時の3項目） */
type PatientRequiredHighlightKey =
  | 'name'
  | 'nameRomaji'
  | 'guardianName'
  | 'guardianNameRomaji'
  | 'guardianContact';

interface PatientFormPanelProps {
  isMobile?: boolean;
}

export const PatientFormPanel: React.FC<PatientFormPanelProps> = ({ isMobile = false }) => {
  // 【追加】申し送りパネルを開くための関数を取得
  const { 
    setShowPatientFormPanel,
    clearEditingPatientOnly,
    editingPatient, 
    addPatient, 
    updatePatient,
    deletePatient, // ★ 削除関数を取得
    currentUser, // 【追加】現在のユーザー情報を取得
    patientFormRevision, // ★ 更新用のカウンターを取得
    isUpdatingPatient, // ★ 更新中のローディング状態を取得
    isAddingPatient, // ★ 新規追加中のローディング状態を取得
    isDeletingPatient, // ★ 削除中のローディング状態を取得
    allStaff, // ★ 全スタッフのリストを取得
    isNavigatingBack,
    navigateBackToPatientList,
  } = useDashboardStore();
  const isEditing = !!editingPatient;

  // フォームの各入力値をstateで管理
  const [patientNumber, setPatientNumber] = useState('');
  const [name, setName] = useState('');
  const [nameRomaji, setNameRomaji] = useState('');
  const [patientPhoneNumber, setPatientPhoneNumber] = useState('');
  const [hasGuardian, setHasGuardian] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [guardianNameRomaji, setGuardianNameRomaji] = useState('');
  const [guardianContact, setGuardianContact] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // ★ 削除確認ダイアログの表示状態
  const [successKind, setSuccessKind] = useState<'add' | 'update' | 'delete' | null>(null);
  const [patientFormError, setPatientFormError] = useState<string | null>(null);
  const [requiredFieldHighlight, setRequiredFieldHighlight] = useState<Set<PatientRequiredHighlightKey>>(
    () => new Set()
  );

  const isRequiredInvalid = (key: PatientRequiredHighlightKey) => requiredFieldHighlight.has(key);
  const requiredLabelClass = (key: PatientRequiredHighlightKey) =>
    `block text-sm font-medium ${isRequiredInvalid(key) ? 'text-red-700' : 'text-gray-700'}`;
  const requiredInputClass = (key: PatientRequiredHighlightKey) =>
    `mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
      isRequiredInvalid(key) ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
    }`;

  const clearPatientFormValidation = () => {
    setPatientFormError(null);
    setRequiredFieldHighlight(new Set());
  };

  useEffect(() => {
    clearPatientFormValidation();
    if (isEditing && editingPatient) {
      setPatientNumber(editingPatient.patientNumber || '');
      setName(editingPatient.name || '');
      setNameRomaji(editingPatient.nameRomaji || '');
      setPatientPhoneNumber(editingPatient.patientPhoneNumber || '');
      setHasGuardian(editingPatient.hasGuardian || false);
      setGuardianName(editingPatient.guardianName || '');
      setGuardianNameRomaji(editingPatient.guardianNameRomaji || '');
      setGuardianContact(editingPatient.guardianContact || '');
      setGuardianRelationship(editingPatient.guardianRelationship || '');
      setZipcode(editingPatient.zipcode || '');
      setAddress(editingPatient.address || '');
    } else {
      // 新規登録モードの時はフォームを空にする（患者番号は自動生成）
      setPatientNumber(generatePatientNumber());
      setName('');
      setNameRomaji('');
      setPatientPhoneNumber('');
      setHasGuardian(false);
      setGuardianName('');
      setGuardianNameRomaji('');
      setGuardianContact('');
      setGuardianRelationship('');
      setZipcode('');
      setAddress('');
    }
  }, [isEditing, editingPatient]);

  // 【重要】住所自動入力の処理
  const handleZipcodeSearch = async (zip: string) => {
    if (zip.length !== 7) return;

    setIsLoading(true);
    try {
      const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`);
      const data = await response.json();
      
      if (data.status === 200 && data.results) {
        const result = data.results[0];
        const fullAddress = `${result.address1}${result.address2}${result.address3}`;
        setAddress(fullAddress);
      } else {
        // 住所が見つからなかった場合、ユーザーに通知するかは検討
        console.warn('住所が見つかりませんでした。');
      }
    } catch (error) {
      console.error('住所の取得に失敗しました:', error);
      // エラーが発生した場合も、ユーザーに通知するかは検討
    } finally {
      setIsLoading(false);
    }
  };
  
  // 郵便番号が変更されたときに住所検索を実行
  useEffect(() => {
    // ハイフンを除外して数字のみ7桁かチェック
    const formattedZip = zipcode.replace(/-/g, '');
    if (formattedZip.length === 7) {
      handleZipcodeSearch(formattedZip);
    }
  }, [zipcode]);


  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!isEditing || !editingPatient) return;
    const id = editingPatient.id;
    setShowDeleteConfirm(false);
    const ok = await deletePatient(id);
    if (!ok) {
      alert('患者の削除に失敗しました。');
      return;
    }
    if (isMobile) {
      setSuccessKind('delete');
    } else {
      setShowPatientFormPanel(false);
      clearEditingPatientOnly();
    }
  };

  // 患者番号の自動生成（8桁を0000-0000形式に）
  const generatePatientNumber = (): string => {
    // 現在のタイムスタンプから8桁を生成（簡易版）
    const timestamp = Date.now().toString();
    const last8Digits = timestamp.slice(-8);
    return `${last8Digits.slice(0, 4)}-${last8Digits.slice(4, 8)}`;
  };

  // 【重要】「保存する」ボタンがクリックされた時の処理
  const handleSave = async () => {
    const nameTrimmed = name.trim();
    const nameRomajiTrimmed = nameRomaji.trim();
    const guardianNameT = hasGuardian ? guardianName.trim() : undefined;
    const guardianNameRomajiT = hasGuardian ? guardianNameRomaji.trim() : undefined;
    const guardianContactT = hasGuardian ? guardianContact.trim() : undefined;
    const guardianRelationshipT = hasGuardian ? guardianRelationship.trim() || undefined : undefined;

    const missing = new Set<PatientRequiredHighlightKey>();
    if (!nameTrimmed) missing.add('name');
    if (!nameRomajiTrimmed) missing.add('nameRomaji');
    if (hasGuardian) {
      if (!guardianNameT) missing.add('guardianName');
      if (!guardianNameRomajiT) missing.add('guardianNameRomaji');
      if (!guardianContactT) missing.add('guardianContact');
    }
    if (missing.size > 0) {
      setRequiredFieldHighlight(new Set(missing));
      setPatientFormError('必須項目が未入力です。');
      return;
    }
    clearPatientFormValidation();

    if (isEditing && editingPatient) {
      // 編集モードの場合
      const updatedData = {
        name: nameTrimmed,
        nameRomaji: nameRomajiTrimmed,
        patientPhoneNumber: patientPhoneNumber,
        hasGuardian: hasGuardian,
        guardianName: guardianNameT,
        guardianNameRomaji: guardianNameRomajiT,
        guardianContact: guardianContactT,
        guardianRelationship: guardianRelationshipT,
        zipcode: zipcode,
        address: address,
        // updated_by はストア側で自動設定
      };
      
      const ok = await updatePatient(editingPatient.id, updatedData);
      if (ok) {
        clearPatientFormValidation();
        if (isMobile) {
          setSuccessKind('update');
        } else {
          setShowPatientFormPanel(false);
        }
      } else {
        alert('患者情報の更新に失敗しました。');
      }
    } else {
      // 新規登録モードの場合
      const generatedPatientNumber = generatePatientNumber();
      // 患者番号をstateに保存（保存後に表示されるように）
      setPatientNumber(generatedPatientNumber);
      const newPatientData: NewPatientData = {
        name: nameTrimmed,
        nameRomaji: nameRomajiTrimmed,
        patientNumber: generatedPatientNumber,
        patientPhoneNumber: patientPhoneNumber,
        hasGuardian: hasGuardian,
        guardianName: guardianNameT,
        guardianNameRomaji: guardianNameRomajiT,
        guardianContact: guardianContactT,
        guardianRelationship: guardianRelationshipT,
        zipcode: zipcode,
        address: address,
      };
      
      // ストアのaddPatientを呼び出し、データベースへの保存を待つ
      const result = await addPatient(newPatientData);

      if (result) {
        clearPatientFormValidation();
        console.log('新しい患者が追加されました:', result);
        if (isMobile) {
          setSuccessKind('add');
        } else {
          setShowPatientFormPanel(false);
        }
      } else {
        // 失敗した場合は、ユーザーに通知する
        alert('患者の登録に失敗しました。');
      }
    }
  };

  // 申し送り機能は患者編集パネルからは開かない（README仕様に準拠）

  const handleCloseSuccessOverlay = () => {
    setSuccessKind(null);
    navigateBackToPatientList();
  };

  const successTitle =
    successKind === 'add'
      ? '患者追加完了'
      : successKind === 'update'
        ? '患者情報を更新しました'
        : successKind === 'delete'
          ? '患者を削除しました'
          : '';

  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const patientFormSwipeRef = useRef<HTMLDivElement>(null);
  const handlePatientFormEdgeBack = useCallback(() => {
    navigateBackToPatientList();
  }, [navigateBackToPatientList]);
  const { swipeStyle: patientFormSwipeStyle } = useMobileEdgeSwipeBack(patientFormSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile && !successKind && !showDeleteConfirm,
    onNavigateBack: handlePatientFormEdgeBack,
  });

  return (
    <>
      {successKind && isMobile && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={handleCloseSuccessOverlay}>
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2 text-gray-800">
              <p className="font-semibold text-green-600">{successTitle}</p>
            </div>
            <Button
              type="button"
              variant="cta"
              className="mt-4 w-full"
              onClick={handleCloseSuccessOverlay}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    <div
      ref={patientFormSwipeRef}
      style={isMobile ? patientFormSwipeStyle : undefined}
      className={isMobile ? 'fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-50' : 'contents'}
    >
    <motion.div
      variants={panelVariants}
      initial={isMobile && isNavigatingBack ? "hiddenBack" : "hidden"}
      animate="visible"
      exit={
        isMobile
          ? isNavigatingBack
            ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
            : { x: '-100%' }
          : undefined
      }
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? "h-full w-full bg-white flex flex-col shadow-lg" 
        : "panel-col panel-col--settings z-20 bg-white border-r flex flex-col shadow-lg relative"
      }
    >
      {(isUpdatingPatient || isAddingPatient || isDeletingPatient) && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
        </div>
      )}
      <div className="flex items-center justify-between p-4 border-b relative z-20 flex-shrink-0">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                navigateBackToPatientList();
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {isEditing ? `患者情報編集` : '新規患者登録'}
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">
              {isEditing ? `患者情報編集` : '新規患者登録'}
            </h2>
          )}
        </div>
      </div>

      <motion.div
        key={patientFormRevision}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={`flex-1 overflow-y-auto p-6 space-y-6 ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}
      >
        {/* 患者番号（自動生成、読み取り専用） */}
        <div>
          <label htmlFor="patientNumber" className="block text-sm font-medium text-gray-700">患者番号</label>
          <input
            type="text"
            id="patientNumber"
            value={patientNumber || (!isEditing ? generatePatientNumber() : '')}
            readOnly
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>
        <div>
          <label htmlFor="name" className={requiredLabelClass('name')}>
            名前 <RequiredMark />
          </label>
          <input
            type="text"
            id="name"
            placeholder="例）山田太郎"
            value={name}
            onChange={(e) => {
              clearPatientFormValidation();
              setName(e.target.value);
            }}
            className={requiredInputClass('name')}
          />
        </div>
        <div>
          <label htmlFor="nameRomaji" className={requiredLabelClass('nameRomaji')}>
            ローマ字（検索用） <RequiredMark />
          </label>
          <input
            type="text"
            id="nameRomaji"
            placeholder="例）yamada taro"
            value={nameRomaji}
            onChange={(e) => {
              clearPatientFormValidation();
              setNameRomaji(e.target.value);
            }}
            className={requiredInputClass('nameRomaji')}
          />
        </div>
        {/* 患者電話番号（常に表示） */}
        <div>
          <label htmlFor="patientPhoneNumber" className="block text-sm font-medium text-gray-700">患者電話番号</label>
          <input
            type="tel"
            id="patientPhoneNumber"
            placeholder="0901231234"
            value={patientPhoneNumber}
            onChange={(e) => {
              clearPatientFormValidation();
              // 数字のみ許可（ハイフンなし）
              const value = e.target.value.replace(/\D/g, '');
              setPatientPhoneNumber(value);
            }}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {/* 保護者ありチェックボックス */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasGuardian"
            checked={hasGuardian}
            onChange={(e) => {
              clearPatientFormValidation();
              setHasGuardian(e.target.checked);
              // チェック解除時は保護者情報をクリア
              if (!e.target.checked) {
                setGuardianName('');
                setGuardianNameRomaji('');
                setGuardianContact('');
                setGuardianRelationship('');
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="hasGuardian" className="ml-2 block text-sm font-medium text-gray-700">
            保護者あり
          </label>
        </div>
        {/* 保護者情報セクション（チェック時のみ表示） */}
        <AnimatePresence>
          {hasGuardian && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-4 p-4 border border-gray-200 rounded-md bg-gray-50 overflow-hidden"
            >
              <h3 className="text-sm font-semibold text-gray-700 mb-2">保護者情報</h3>
              <div>
                <label htmlFor="guardianName" className={requiredLabelClass('guardianName')}>
                  保護者名 <RequiredMark />
                </label>
                <input
                  type="text"
                  id="guardianName"
                  placeholder="例）山田花子"
                  value={guardianName}
                  onChange={(e) => {
                    clearPatientFormValidation();
                    setGuardianName(e.target.value);
                  }}
                  className={requiredInputClass('guardianName')}
                />
              </div>
              <div>
                <label htmlFor="guardianNameRomaji" className={requiredLabelClass('guardianNameRomaji')}>
                  ローマ字 <RequiredMark />
                </label>
                <input
                  type="text"
                  id="guardianNameRomaji"
                  placeholder="例）yamada hanako"
                  value={guardianNameRomaji}
                  onChange={(e) => {
                    clearPatientFormValidation();
                    setGuardianNameRomaji(e.target.value);
                  }}
                  className={requiredInputClass('guardianNameRomaji')}
                />
              </div>
              <div>
                <label htmlFor="guardianContact" className={requiredLabelClass('guardianContact')}>
                  連絡先 <RequiredMark />
                </label>
                <input
                  type="tel"
                  id="guardianContact"
                  placeholder="0901231234"
                  value={guardianContact}
                  onChange={(e) => {
                    clearPatientFormValidation();
                    // 数字のみ許可（ハイフンなし）
                    const value = e.target.value.replace(/\D/g, '');
                    setGuardianContact(value);
                  }}
                  className={requiredInputClass('guardianContact')}
                />
              </div>
              <div>
                <label htmlFor="guardianRelationship" className="block text-sm font-medium text-gray-700">関係（任意）</label>
                <input
                  type="text"
                  id="guardianRelationship"
                  placeholder="例）母、父、祖母など"
                  value={guardianRelationship}
                  onChange={(e) => {
                    clearPatientFormValidation();
                    setGuardianRelationship(e.target.value);
                  }}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div>
          <label htmlFor="zipcode" className="block text-sm font-medium text-gray-700">郵便番号</label>
          <div className="mt-1 relative">
            <input
              type="text"
              id="zipcode"
              placeholder="例）1000001"
              value={zipcode}
              onChange={(e) => {
                clearPatientFormValidation();
                setZipcode(e.target.value);
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {isLoading && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700">住所</label>
          <textarea
            id="address"
            rows={3}
            value={address}
            onChange={(e) => {
              clearPatientFormValidation();
              setAddress(e.target.value);
            }}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {isEditing && editingPatient && (
          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-sm font-medium text-white mb-2">履歴情報</h3>
            <div className="space-y-2 text-xs text-white">
              <div>
                <span className="font-medium">作成日時:</span> {new Date(editingPatient.createdAt).toLocaleString('ja-JP')}
              </div>
              <div>
                <span className="font-medium">作成者:</span> {editingPatient.createdByName || allStaff.find(s => s.id === editingPatient.createdBy)?.name || editingPatient.createdBy || '不明'}
              </div>
              <div>
                <span className="font-medium">最終更新日時:</span> {new Date(editingPatient.updatedAt).toLocaleString('ja-JP')}
              </div>
              <div>
                <span className="font-medium">最終更新者:</span> {editingPatient.updatedByName || allStaff.find(s => s.id === editingPatient.updatedBy)?.name || editingPatient.updatedBy || '不明'}
              </div>
            </div>
          </div>
        )}

        {/* 申し送りボタンは患者編集パネルからは表示しない（仕様） */}

        {/* 保存・キャンセルボタン */}
        {patientFormError && (
          <p className="text-sm text-red-600" role="alert">
            {patientFormError}
          </p>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          {/* 編集モードかつ管理者権限以上の場合のみ削除ボタンを表示 */}
          {isEditing && currentUser && (currentUser.role === 'admin' || currentUser.role === 'developer') && (
            <Button
              type="button"
              onClick={handleDeleteClick}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              削除する
            </Button>
          )}
          {/* 管理者権限以上の場合のみ保存/更新ボタンを表示 */}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'developer') && (
            <Button type="button" variant="cta" onClick={handleSave}>
              {isEditing ? '更新する' : '保存する'}
            </Button>
          )}
          {/* 
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            閉じる
          </button>
           */}
        </div>
      </motion.div>
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>本当に削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              患者「{editingPatient?.name}」を削除します。この操作は元に戻すことはできません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
    </div>
    </>
  );
};
